/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react'
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from 'react-simple-maps'
import brazilGeo from '../../assets/raw/brazil-states.geojson'
import brazilCitiesCoords from '../../assets/raw/brazil-cities-coords.json'
import {
  FiPlus,
  FiMinus,
  FiRotateCcw,
  FiMapPin,
  FiMousePointer,
  FiArrowLeft,
  FiBarChart2,
  FiTruck,
  FiCheckCircle,
  FiMaximize2,
  FiMinimize2,
  FiUsers,
  FiSearch,
  FiX,
  FiTrendingUp,
  FiCompass,
  FiArrowRight,
} from 'react-icons/fi'
import { FaTrophy } from 'react-icons/fa6'
import api from '../../services/axios'
import { CityDetailsModal, CityMapPoint, CityPdvData } from './CityDetailsModal'
import {
  MapBrazilProps,
  RegionKey,
  REGIONS,
  UF_METADATA,
  UF_COORDINATES,
  UF_POPULATION_IBGE,
  getUfPotencialIbge,
  getStateViewport,
} from './types'
import {
  MapContainer,
  HeaderBar,
  KpiGroup,
  KpiBadge,
  RegionSelector,
  RegionButton,
  FloatingZoomControls,
  ZoomBtn,
  TooltipContainer,
  TooltipHeader,
  TooltipBody,
  TooltipFooter,
  LegendBox,
  MinimizeButton,
  FullscreenButton,
  NorthSouthCard,
  StateSidePanel,
  PanelToggleBtn,
  PanelHeader,
  PanelBody,
  PanelRegionBar,
  PanelRegionPillBtn,
  ActionRecommendationCard,
  DemographicKpiBox,
  CityMacroBadge,
  CityCardItem,
} from './styles'

const normalizeCity = (name: string): string => {
  return (name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase()
}

export const MapBrazil: React.FC<MapBrazilProps> = ({
  data,
  onStateClick,
  isVolumeMode = false,
  unitLabel = 'PDVs Ativos',
  selectedUf,
  filters,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)

  // Normalização de dados dos estados (suporta Map e Record<string, number>)
  const { safeMap, totalValue, activeUfCount, leaderUf } = useMemo(() => {
    const map = new Map<string, number>()

    if (data instanceof Map) {
      data.forEach((val, key) => {
        const num = typeof val === 'number' ? val : (val as any)?.length || 0
        map.set(String(key).trim().toUpperCase(), num)
      })
    } else if (data && typeof data === 'object') {
      Object.entries(data).forEach(([key, val]) => {
        const num = typeof val === 'number' ? val : (val as any)?.length || 0
        map.set(String(key).trim().toUpperCase(), num)
      })
    }

    let sum = 0
    let activeCount = 0
    let max = 0
    let topUf = ''

    map.forEach((val, uf) => {
      if (val > 0) {
        sum += val
        activeCount += 1
        if (val > max) {
          max = val
          topUf = uf
        }
      }
    })

    return {
      safeMap: map,
      totalValue: sum,
      activeUfCount: activeCount,
      leaderUf: topUf,
    }
  }, [data])

  // Top 3 estados para badges com destaque
  const top3Ufs = useMemo(() => {
    return Array.from(safeMap.entries())
      .filter(([_, count]) => count > 0)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([uf]) => uf)
  }, [safeMap])

  // Cobertura e Potencial agregados por Macrorregião (Mapa de Calor Regional)
  const regionalHeatmap = useMemo(() => {
    const totals: Record<RegionKey, { active: number; potencial: number }> = {
      BR: { active: 0, potencial: 0 },
      SE: { active: 0, potencial: 0 },
      S: { active: 0, potencial: 0 },
      NE: { active: 0, potencial: 0 },
      CO: { active: 0, potencial: 0 },
      N: { active: 0, potencial: 0 },
    }

    Object.entries(UF_METADATA).forEach(([uf, meta]) => {
      const active = safeMap.get(uf) || 0
      const pot = getUfPotencialIbge(uf)

      totals.BR.active += active
      totals.BR.potencial += pot

      if (totals[meta.regionKey]) {
        totals[meta.regionKey].active += active
        totals[meta.regionKey].potencial += pot
      }
    })

    const result: Record<RegionKey, { active: number; potencial: number; pct: number }> = {
      BR: { active: 0, potencial: 0, pct: 0 },
      SE: { active: 0, potencial: 0, pct: 0 },
      S: { active: 0, potencial: 0, pct: 0 },
      NE: { active: 0, potencial: 0, pct: 0 },
      CO: { active: 0, potencial: 0, pct: 0 },
      N: { active: 0, potencial: 0, pct: 0 },
    }

    ;(Object.keys(totals) as RegionKey[]).forEach((key) => {
      const t = totals[key]
      const pct = t.potencial > 0 ? (t.active / t.potencial) * 100 : 0
      result[key] = {
        active: t.active,
        potencial: t.potencial,
        pct,
      }
    })
    return result
  }, [safeMap])

  // Regra de cores do IBGE para o mapa macro
  const getStateColor = useCallback((count: number, uf: string) => {
    if (count === 0) return '#2a354b' // Inativo / sem clientes no período - grafite claro visível

    const potencial = getUfPotencialIbge(uf)
    const pct = potencial > 0 ? (count / potencial) * 100 : 0

    if (pct >= 70) return '#166534' // Verde escuro corporativo nítido (+70% do potencial batido)
    if (pct >= 50) return '#22c55e' // Verde vibrante (50% a 69.9% - antes no 30-50%)
    if (pct >= 30) return '#eab308' // Amarelo corporativo (30% a 49.9%)
    if (pct >= 15) return '#f97316' // Laranja corporativo (15% a 29.9%)
    return '#ef4444' // Vermelho corporativo (< 15% - crítico/baixo)
  }, [])

  // Estado de controle de Zoom e Pan
  const [position, setPosition] = useState<{ coordinates: [number, number]; zoom: number }>({
    coordinates: REGIONS.BR.center,
    zoom: REGIONS.BR.zoom,
  })
  const [activeRegion, setActiveRegion] = useState<RegionKey>('BR')

  // Estado de UF focada e lista de Cidades
  const [focusedUf, setFocusedUf] = useState<string | null>(null)
  const [stateCities, setStateCities] = useState<CityMapPoint[]>([])
  const [loadingCities, setLoadingCities] = useState(false)
  const [selectedCity, setSelectedCity] = useState<CityMapPoint | null>(null)

  // Painel Integrado de Inteligência Regional e Demográfica
  const [showSidePanel, setShowSidePanel] = useState(false)
  const [panelRegionFilter, setPanelRegionFilter] = useState<
    'TODOS' | 'Norte' | 'Centro' | 'Sul'
  >('TODOS')
  const [panelCitySearch, setPanelCitySearch] = useState('')

  // Detecção de viewport Desktop vs Mobile para calibração de enquadramento
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true,
  )

  // Estado de tela cheia e animação de expansão/minimização
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isShrinking, setIsShrinking] = useState(false)

  const handleToggleSidePanel = useCallback(
    (open: boolean) => {
      setShowSidePanel(open)
      if (focusedUf) {
        const pc = typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
        const nextPos = getStateViewport(focusedUf, pc, open)
        setPosition(nextPos)
      }
    },
    [focusedUf],
  )

  useEffect(() => {
    const handleResize = () => {
      const pc = window.innerWidth >= 1024
      setIsDesktop(pc)
      if (focusedUf) {
        const nextPos = getStateViewport(focusedUf, pc, showSidePanel)
        setPosition(nextPos)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [focusedUf, showSidePanel])

  const handleMinimize = useCallback(() => {
    setIsShrinking(true)
    setShowSidePanel(false)
    setFocusedUf(null)
    setSelectedCity(null)
    setPanelRegionFilter('TODOS')
    setPanelCitySearch('')
    setActiveRegion('BR')
    setPosition({
      coordinates: REGIONS.BR.center,
      zoom: REGIONS.BR.zoom,
    })
    setTimeout(() => {
      setIsFullscreen(false)
      setIsShrinking(false)
    }, 320)
  }, [])

  const handleToggleFullscreen = useCallback(() => {
    if (isFullscreen) {
      handleMinimize()
    } else {
      setIsFullscreen(true)
      if (focusedUf) {
        const pc = typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
        const nextPos = getStateViewport(focusedUf, pc, showSidePanel)
        setPosition(nextPos)
      }
    }
  }, [isFullscreen, handleMinimize, focusedUf, showSidePanel])

  // Listener para fechar tela cheia com a tecla ESC
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        handleMinimize()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isFullscreen, handleMinimize])

  // Dados brutos agregados do estado focado para gráficos e listas
  const [rawStateData, setRawStateData] = useState<{
    products: any[]
    clients: any[]
    sellout: any[]
    distribuidores: any[]
  } | null>(null)

  // Tooltip de Estado (macro)
  const [tooltip, setTooltip] = useState<{
    visible: boolean
    x: number
    y: number
    uf: string
    name: string
    region: string
    count: number
    potencial: number
    potencialPct: number
    share: number
  }>({
    visible: false,
    x: 0,
    y: 0,
    uf: '',
    name: '',
    region: '',
    count: 0,
    potencial: 0,
    potencialPct: 0,
    share: 0,
  })

  // Tooltip de Cidade individual no Hover
  const [cityHover, setCityHover] = useState<{
    visible: boolean
    x: number
    y: number
    city: CityMapPoint | null
  }>({
    visible: false,
    x: 0,
    y: 0,
    city: null,
  })

  // Carregar Cidades e PDVs da UF focada
  useEffect(() => {
    if (!focusedUf) {
      setStateCities([])
      setRawStateData(null)
      return
    }

    let isMounted = true
    const fetchCityData = async () => {
      setLoadingCities(true)
      try {
        const queryParams: any = { uf: focusedUf, ...filters }
        if (queryParams.month !== undefined && queryParams.month !== null) {
          if (Number(queryParams.month) === -1) {
            queryParams.month = null
          } else {
            queryParams.month = Number(queryParams.month) + 1
          }
        }
        if (queryParams.endMonth !== undefined && queryParams.endMonth !== null) {
          if (Number(queryParams.endMonth) === -1) {
            queryParams.endMonth = null
          } else {
            queryParams.endMonth = Number(queryParams.endMonth) + 1
          }
        }

        const response = await api.get('/get/sellout/details', { params: queryParams })
        if (!isMounted) return

        const {
          clients = [],
          sellout = [],
          distribuidores = [],
          products = [],
        } = response.data || {}

        setRawStateData({ clients, sellout, distribuidores, products })

        const distMap = new Map<string, string>()
        distribuidores.forEach((d: any) =>
          distMap.set(String(d.id).trim(), d.name || `Dist. ${d.id}`),
        )

        const prodMap = new Map<string, any>()
        products.forEach((p: any) => {
          if (p.ean13) prodMap.set(String(p.ean13).trim(), p)
          if (p.dun14) prodMap.set(String(p.dun14).trim(), p)
          if (p.ean) prodMap.set(String(p.ean).trim(), p)
        })

        const clientMap = new Map<string, any>()
        clients.forEach((c: any) => clientMap.set(String(c.cnpj_cpf_pdv).trim(), c))

        // 1. Agrupar sellout por PDV (com contagem de SKUs únicos por PDV)
        const pdvsAgg = new Map<string, CityPdvData & { skuSet: Set<string> }>()
        sellout.forEach((item: any) => {
          const cnpj = String(item.client).trim()
          const cObj = clientMap.get(cnpj)
          const prodCode = String(item.produto).trim()
          const pObj = prodMap.get(prodCode)
          const qty = Number(item.qnt) || 0
          const val = Number(item.valor || 0) * qty

          if (!pdvsAgg.has(cnpj)) {
            const distId = String(
              cObj?.distribuidor_id || cObj?.dist_id || item.dist_id || '',
            ).trim()
            pdvsAgg.set(cnpj, {
              cnpj,
              name: cObj?.nm_client || `PDV: ${cnpj}`,
              city: cObj?.city || cObj?.cidade || cObj?.municipio || 'Não informada',
              bairro: cObj?.bairro || 'Não informado',
              address: cObj?.address || (cObj as any)?.endereco || 'Não informado',
              cep: cObj?.cep || '',
              uf: focusedUf,
              distName: distMap.get(distId) || (distId ? `Dist. ${distId}` : 'Não informado'),
              distId,
              totalValue: 0,
              totalVolume: 0,
              transactionsCount: 0,
              skuCount: 0,
              skuSet: new Set<string>(),
              products: [],
            })
          }

          const entry = pdvsAgg.get(cnpj)!
          entry.totalValue += val
          entry.totalVolume += qty
          entry.transactionsCount += 1
          if (prodCode) entry.skuSet.add(prodCode)

          if (entry.products && entry.products.length < 25) {
            entry.products.push({
              code: prodCode,
              name: pObj?.descricao || `Produto ${prodCode}`,
              qty,
              value: val,
            })
          }
        })

        pdvsAgg.forEach((pdv) => {
          pdv.skuCount = pdv.skuSet.size
        })

        // 2. Agrupar PDVs por Cidade (Município)
        const citiesMap = new Map<
          string,
          {
            cityDisplayName: string
            pdvs: CityPdvData[]
            citySkus: Set<string>
            totalValue: number
            totalVolume: number
          }
        >()

        pdvsAgg.forEach((pdv) => {
          const normCity = normalizeCity(pdv.city)
          if (!citiesMap.has(normCity)) {
            citiesMap.set(normCity, {
              cityDisplayName: pdv.city,
              pdvs: [],
              citySkus: new Set<string>(),
              totalValue: 0,
              totalVolume: 0,
            })
          }

          const cEntry = citiesMap.get(normCity)!
          cEntry.pdvs.push(pdv)
          cEntry.totalValue += pdv.totalValue
          cEntry.totalVolume += pdv.totalVolume
          pdv.skuSet.forEach((s) => cEntry.citySkus.add(s))
        })

        const citiesRaw = Array.from(citiesMap.entries())
        if (citiesRaw.length === 0) {
          setStateCities([])
          return
        }

        // Ordenar cidades por volume/faturamento decrescente
        citiesRaw.sort((a, b) =>
          isVolumeMode
            ? b[1].totalVolume - a[1].totalVolume
            : b[1].totalValue - a[1].totalValue,
        )

        const maxCityVol = isVolumeMode
          ? citiesRaw[0]?.[1].totalVolume || 1
          : citiesRaw[0]?.[1].totalValue || 1

        const coordsLookup = brazilCitiesCoords as unknown as Record<string, [number, number]>
        const ufCoordBase = UF_COORDINATES[focusedUf] || [-50, -15]

        // Identificar limites de latitude para classificar em Norte, Centro e Sul
        const allLats = citiesRaw
          .map(([normCity]) => {
            const key = `${focusedUf}_${normCity}`
            const c = coordsLookup[key] || ufCoordBase
            return c[1]
          })
          .filter((l) => typeof l === 'number' && !isNaN(l))

        const minLat = allLats.length > 0 ? Math.min(...allLats) : -10
        const maxLat = allLats.length > 0 ? Math.max(...allLats) : -2
        const latRange = maxLat - minLat || 0.001

        const totalStatePop = UF_POPULATION_IBGE[focusedUf.toUpperCase()] || 6000000
        const totalStateVolSum =
          citiesRaw.reduce(
            (acc, [_, c]) => acc + (isVolumeMode ? c.totalVolume : c.totalValue),
            0,
          ) || 1
        const totalStatePdvsSum =
          citiesRaw.reduce((acc, [_, c]) => acc + c.pdvs.length, 0) || 1

        const mappedCities: CityMapPoint[] = citiesRaw.map(([normCity, cData]) => {
          const key = `${focusedUf}_${normCity}`
          const coord = coordsLookup[key] || ufCoordBase
          const activePdvsCount = cData.pdvs.length
          const avgTicket =
            activePdvsCount > 0
              ? isVolumeMode
                ? cData.totalVolume / activePdvsCount
                : cData.totalValue / activePdvsCount
              : 0

          const sumSkus = cData.pdvs.reduce((acc, p) => acc + p.skuCount, 0)
          const avgSkusPerPdv = activePdvsCount > 0 ? sumSkus / activePdvsCount : 0

          const ratio = isVolumeMode
            ? cData.totalVolume / maxCityVol
            : cData.totalValue / maxCityVol
          const tier: 'top' | 'mid' | 'low' =
            ratio >= 0.35 ? 'top' : ratio >= 0.1 ? 'mid' : 'low'

          // Classificação regional interna do estado (Norte, Centro ou Sul)
          const latRatio = (coord[1] - minLat) / latRange
          const macroRegion: 'Norte' | 'Centro' | 'Sul' =
            latRatio >= 0.6 ? 'Norte' : latRatio >= 0.35 ? 'Centro' : 'Sul'

          // Demografia IBGE e Potencial de PDVs (1 PDV / 1.000 habitantes)
          const cityWeight =
            ((isVolumeMode ? cData.totalVolume : cData.totalValue) / totalStateVolSum) * 0.6 +
            (activePdvsCount / totalStatePdvsSum) * 0.4
          const population = Math.max(12000, Math.round(totalStatePop * cityWeight))
          const potencialPdvs = Math.max(activePdvsCount, Math.round(population / 1000))
          const potencialPct =
            potencialPdvs > 0 ? Math.round((activePdvsCount / potencialPdvs) * 100) : 0

          let growthStatus: 'high_opportunity' | 'expanding' | 'consolidated' = 'consolidated'
          if (potencialPct < 50 || activePdvsCount <= 2) {
            growthStatus = 'high_opportunity'
          } else if (potencialPct < 70 || avgSkusPerPdv < 5) {
            growthStatus = 'expanding'
          }

          cData.pdvs.sort((a, b) =>
            isVolumeMode ? b.totalVolume - a.totalVolume : b.totalValue - a.totalValue,
          )

          return {
            city: cData.cityDisplayName,
            uf: focusedUf,
            coordinates: coord,
            activePdvsCount,
            totalValue: cData.totalValue,
            totalVolume: cData.totalVolume,
            avgTicket,
            avgSkusPerPdv,
            uniqueSkusCount: cData.citySkus.size,
            tier,
            growthStatus,
            pdvs: cData.pdvs,
            macroRegion,
            population,
            potencialPdvs,
            potencialPct,
          }
        })

        setStateCities(mappedCities)
      } catch (err) {
        console.error('Erro ao buscar Cidades da UF:', err)
      } finally {
        if (isMounted) setLoadingCities(false)
      }
    }

    fetchCityData()

    return () => {
      isMounted = false
    }
  }, [focusedUf, filters, isVolumeMode])

  // Totalizadores do estado focado
  const stateTotalVol = useMemo(() => {
    return stateCities.reduce((acc, c) => acc + c.totalVolume, 0)
  }, [stateCities])

  const stateTotalVal = useMemo(() => {
    return stateCities.reduce((acc, c) => acc + c.totalValue, 0)
  }, [stateCities])

  const stateTotalPdvs = useMemo(() => {
    return stateCities.reduce((acc, c) => acc + c.activePdvsCount, 0)
  }, [stateCities])

  const stateAvgTicket = useMemo(() => {
    if (stateTotalPdvs === 0) return 0
    return isVolumeMode ? stateTotalVol / stateTotalPdvs : stateTotalVal / stateTotalPdvs
  }, [stateTotalPdvs, stateTotalVol, stateTotalVal, isVolumeMode])

  // Cálculo do gradiente de calor Norte-Sul desenhado diretamente no polígono do estado
  const stateHeatGradient = useMemo(() => {
    if (!stateCities || stateCities.length === 0) {
      return {
        northColor: '#10b981',
        centerNorthColor: '#22c55e',
        centerSouthColor: '#84cc16',
        southColor: '#1e293b',
        northPct: 33,
        centerPct: 34,
        southPct: 33,
        northPdvs: 0,
        centerPdvs: 0,
        southPdvs: 0,
      }
    }

    const lats = stateCities
      .map((c) => c.coordinates[1])
      .filter((l) => typeof l === 'number' && !isNaN(l))

    if (lats.length === 0) {
      return {
        northColor: '#10b981',
        centerNorthColor: '#22c55e',
        centerSouthColor: '#84cc16',
        southColor: '#1e293b',
        northPct: 33,
        centerPct: 34,
        southPct: 33,
        northPdvs: 0,
        centerPdvs: 0,
        southPdvs: 0,
      }
    }

    const minLat = Math.min(...lats)
    const maxLat = Math.max(...lats)
    const latRange = maxLat - minLat || 0.001

    let northPdvs = 0
    let centerNorthPdvs = 0
    let centerSouthPdvs = 0
    let southPdvs = 0
    let totalPdvs = 0

    stateCities.forEach((c) => {
      const lat = c.coordinates[1]
      const count = isVolumeMode ? c.totalVolume : c.activePdvsCount
      totalPdvs += count

      const ratio = (lat - minLat) / latRange // 0 = mais ao Sul, 1 = mais ao Norte
      if (ratio >= 0.7) {
        northPdvs += count
      } else if (ratio >= 0.45) {
        centerNorthPdvs += count
      } else if (ratio >= 0.25) {
        centerSouthPdvs += count
      } else {
        southPdvs += count
      }
    })

    const getColorForShare = (share: number) => {
      if (share >= 0.35) return '#10b981' // Alta presença / Meta batida (+35%)
      if (share >= 0.2) return '#22c55e' // Boa cobertura (20-35%)
      if (share >= 0.1) return '#84cc16' // Presença moderada (10-20%)
      if (share >= 0.04) return '#eab308' // Baixa presença (4-10%)
      return '#1e293b' // Sem presença / Gap (<4%)
    }

    const total = totalPdvs || 1
    const northShare = northPdvs / total
    const centerNorthShare = centerNorthPdvs / total
    const centerSouthShare = centerSouthPdvs / total
    const southShare = southPdvs / total

    const northPct = Math.round((northPdvs / total) * 100)
    const centerPct = Math.round(((centerNorthPdvs + centerSouthPdvs) / total) * 100)
    const southPct = Math.round((southPdvs / total) * 100)

    return {
      northColor: getColorForShare(northShare),
      centerNorthColor: getColorForShare(centerNorthShare),
      centerSouthColor: getColorForShare(centerSouthShare),
      southColor: getColorForShare(southShare),
      northPct,
      centerPct,
      southPct,
      northPdvs: Math.round(northPdvs),
      centerPdvs: Math.round(centerNorthPdvs + centerSouthPdvs),
      southPdvs: Math.round(southPdvs),
    }
  }, [stateCities, isVolumeMode])

  // Cidades filtradas pela Macrorregião selecionada (TODOS, Norte, Centro ou Sul)
  const regionFilteredCities = useMemo(() => {
    if (panelRegionFilter === 'TODOS') return stateCities
    return stateCities.filter((c) => c.macroRegion === panelRegionFilter)
  }, [stateCities, panelRegionFilter])

  // Contadores por região para as pílulas de navegação
  const regionCounts = useMemo(() => {
    let norteCid = 0,
      nortePdvs = 0
    let centroCid = 0,
      centroPdvs = 0
    let sulCid = 0,
      sulPdvs = 0

    stateCities.forEach((c) => {
      if (c.macroRegion === 'Norte') {
        norteCid++
        nortePdvs += c.activePdvsCount
      } else if (c.macroRegion === 'Centro') {
        centroCid++
        centroPdvs += c.activePdvsCount
      } else {
        sulCid++
        sulPdvs += c.activePdvsCount
      }
    })

    return {
      norteCid,
      nortePdvs,
      centroCid,
      centroPdvs,
      sulCid,
      sulPdvs,
    }
  }, [stateCities])

  // Métricas agregadas de Potencial IBGE e Share por Macrorregião (Norte, Centro, Sul)
  const regionalBreakdown = useMemo(() => {
    const empty = {
      norte: { pdvs: 0, potencial: 0, atingimento: 0, share: 0 },
      centro: { pdvs: 0, potencial: 0, atingimento: 0, share: 0 },
      sul: { pdvs: 0, potencial: 0, atingimento: 0, share: 0 },
      totalPdvs: 0,
    }
    if (!stateCities || stateCities.length === 0) return empty

    let nPdvs = 0,
      nPot = 0
    let cPdvs = 0,
      cPot = 0
    let sPdvs = 0,
      sPot = 0
    let totalPdvs = 0

    stateCities.forEach((c) => {
      const p = c.activePdvsCount || 0
      const pot = c.potencialPdvs || 0
      totalPdvs += p

      if (c.macroRegion === 'Norte') {
        nPdvs += p
        nPot += pot
      } else if (c.macroRegion === 'Centro') {
        cPdvs += p
        cPot += pot
      } else {
        sPdvs += p
        sPot += pot
      }
    })

    const tot = totalPdvs || 1
    return {
      norte: {
        pdvs: nPdvs,
        potencial: nPot,
        atingimento: nPot > 0 ? Math.round((nPdvs / nPot) * 100) : 0,
        share: Math.round((nPdvs / tot) * 100),
      },
      centro: {
        pdvs: cPdvs,
        potencial: cPot,
        atingimento: cPot > 0 ? Math.round((cPdvs / cPot) * 100) : 0,
        share: Math.round((cPdvs / tot) * 100),
      },
      sul: {
        pdvs: sPdvs,
        potencial: sPot,
        atingimento: sPot > 0 ? Math.round((sPdvs / sPot) * 100) : 0,
        share: Math.round((sPdvs / tot) * 100),
      },
      totalPdvs,
    }
  }, [stateCities])

  // Estatísticas Demográficas e de Potencial da Macrorregião selecionada (ou estado todo)
  const macroRegionStats = useMemo(() => {
    const list = regionFilteredCities
    const totalCities = list.length
    const totalPdvs = list.reduce((acc, c) => acc + c.activePdvsCount, 0)
    const totalVol = list.reduce((acc, c) => acc + c.totalVolume, 0)
    const totalVal = list.reduce((acc, c) => acc + c.totalValue, 0)
    const totalPop = list.reduce((acc, c) => acc + (c.population || 0), 0)
    const totalPotencialPdvs = list.reduce((acc, c) => acc + (c.potencialPdvs || 0), 0)
    const atingimentoPct =
      totalPotencialPdvs > 0 ? Math.round((totalPdvs / totalPotencialPdvs) * 100) : 0
    const isBelow70 = atingimentoPct < 70
    const avgTicket =
      totalPdvs > 0 ? (isVolumeMode ? totalVol / totalPdvs : totalVal / totalPdvs) : 0

    const sumSkus = list.reduce((acc, c) => acc + c.avgSkusPerPdv * c.activePdvsCount, 0)
    const avgSkus = totalPdvs > 0 ? Number((sumSkus / totalPdvs).toFixed(1)) : 0

    let actionRecommendation = ''
    let statusType: 'danger' | 'warning' | 'success' = 'warning'
    let statusLabel = ''

    if (atingimentoPct < 50) {
      statusType = 'danger'
      statusLabel = 'Abaixo de 50% (Crítico)'
      actionRecommendation = `Atingimento de apenas ${atingimentoPct}% do potencial IBGE. Ação Prioritária: Abertura imediata de novos PDVs e credenciamento de distribuidores para preencher o gap demográfico.`
    } else if (atingimentoPct < 70) {
      statusType = 'warning'
      statusLabel = `${atingimentoPct}% (Abaixo de 70% - Alta Oportunidade)`
      actionRecommendation = `Região em expansão com ${atingimentoPct}% atingido (abaixo da meta de 70%). Ação Recomendada: Positivar carteira inativa e ampliar rotas de vendas para alcançar os 70% mínimos.`
    } else {
      statusType = 'success'
      statusLabel = `${atingimentoPct}% (Meta Batida ≥ 70%)`
      actionRecommendation = `Meta de cobertura atingida (${atingimentoPct}% do potencial IBGE). Ação Estratégica: Foco em rentabilidade, aumento de ticket médio e expansão do mix de SKUs por PDV (média atual: ${avgSkus} SKUs).`
    }

    return {
      totalCities,
      totalPdvs,
      totalVol,
      totalVal,
      totalPop,
      totalPotencialPdvs,
      atingimentoPct,
      isBelow70,
      avgTicket,
      avgSkus,
      actionRecommendation,
      statusType,
      statusLabel,
    }
  }, [regionFilteredCities, isVolumeMode])

  // Filtros de busca internos do Painel aplicados sobre a macrorregião selecionada
  const filteredPanelCities = useMemo(() => {
    let list = regionFilteredCities
    if (panelCitySearch.trim()) {
      const q = panelCitySearch.toLowerCase().trim()
      list = list.filter((c) => c.city.toLowerCase().includes(q))
    }
    return list
  }, [regionFilteredCities, panelCitySearch])

  // Handlers de zoom
  const handleZoomIn = useCallback(() => {
    setPosition((prev) => ({
      ...prev,
      zoom: Math.min(Number((prev.zoom * 1.35).toFixed(2)), 6),
    }))
  }, [])

  const handleZoomOut = useCallback(() => {
    setPosition((prev) => {
      const nextZoom = Math.max(Number((prev.zoom / 1.35).toFixed(2)), 0.8)
      if (nextZoom < 2 && focusedUf) {
        setFocusedUf(null)
      }
      return {
        ...prev,
        zoom: nextZoom,
      }
    })
  }, [focusedUf])

  const handleResetZoom = useCallback(() => {
    setFocusedUf(null)
    setShowSidePanel(false)
    setSelectedCity(null)
    setPanelRegionFilter('TODOS')
    setPanelCitySearch('')
    setActiveRegion('BR')
    setPosition({
      coordinates: REGIONS.BR.center,
      zoom: REGIONS.BR.zoom,
    })
  }, [])

  const handleSelectRegion = useCallback((regKey: RegionKey) => {
    setFocusedUf(null)
    setShowSidePanel(false)
    setActiveRegion(regKey)
    const conf = REGIONS[regKey]
    if (conf) {
      setPosition({
        coordinates: conf.center,
        zoom: conf.zoom,
      })
    }
  }, [])

  const handleMoveEnd = useCallback(
    (pos: { coordinates: [number, number]; zoom: number }) => {
      setPosition(pos)
      if (pos.zoom < 1.8 && focusedUf) {
        setFocusedUf(null)
        setShowSidePanel(false)
      }
    },
    [focusedUf],
  )

  // Clique em um estado: foca no estado, ativa tela cheia animada no PC e abre painel de inteligência calibrado
  const handleStateClick = useCallback(
    (sigla: string) => {
      const pc = typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
      setFocusedUf(sigla)
      if (pc) {
        setIsFullscreen(true)
      }
      setShowSidePanel(true)
      setPanelCitySearch('')
      const targetPos = getStateViewport(sigla, pc, true)
      setPosition(targetPos)
    },
    [],
  )

  // Handlers do Tooltip de Estado
  const handleMouseMoveState = useCallback(
    (event: React.MouseEvent, sigla: string) => {
      if (focusedUf) return
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const rawX = event.clientX - rect.left
      const x = Math.max(135, Math.min(rawX, rect.width - 135))
      const y = event.clientY - rect.top

      const count = safeMap.get(sigla) || 0
      const potencial = getUfPotencialIbge(sigla)
      const potencialPct = potencial > 0 ? Number(((count / potencial) * 100).toFixed(1)) : 0
      const share = totalValue > 0 ? (count / totalValue) * 100 : 0
      const meta = UF_METADATA[sigla] || { name: sigla, region: 'Brasil' }

      setTooltip({
        visible: true,
        x,
        y,
        uf: sigla,
        name: meta.name,
        region: meta.region,
        count,
        potencial,
        potencialPct,
        share,
      })
    },
    [safeMap, totalValue, focusedUf],
  )

  const handleMouseLeaveState = useCallback(() => {
    setTooltip((prev) => ({ ...prev, visible: false }))
  }, [])

  // Handlers do Tooltip de Cidade individual
  const handleCityMouseEnter = (event: React.MouseEvent, city: CityMapPoint) => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const rawX = event.clientX - rect.left
    const x = Math.max(140, Math.min(rawX, rect.width - 140))
    const y = event.clientY - rect.top

    setCityHover({
      visible: true,
      x,
      y,
      city,
    })
  }

  const handleCityMouseLeave = () => {
    setCityHover((prev) => ({ ...prev, visible: false }))
  }

  return (
    <MapContainer
      ref={containerRef}
      $isFullscreen={isFullscreen}
      $isShrinking={isShrinking}
    >
      {/* Barra Superior Inteligente */}
      <HeaderBar>
        <KpiGroup>
          {focusedUf ? (
            <>
              <button
                type='button'
                onClick={handleResetZoom}
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: '#ffffff',
                  padding: '5px 12px',
                  borderRadius: 6,
                  fontSize: 11,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
                title='Voltar à Visão Geral do Brasil'
              >
                <FiArrowLeft size={13} /> Voltar ao Brasil
              </button>

              <KpiBadge $highlight={true}>
                <span className='dot' />
                <strong>
                  {focusedUf} ({UF_METADATA[focusedUf]?.name})
                </strong>{' '}
                • {loadingCities ? 'Carregando Cidades...' : `${stateCities.length} Cidades Atendidas`}
              </KpiBadge>

              {onStateClick && (
                <button
                  type='button'
                  onClick={() => onStateClick(focusedUf)}
                  style={{
                    background: 'rgba(166, 193, 11, 0.15)',
                    border: '1px solid rgba(166, 193, 11, 0.4)',
                    color: '#a6c10b',
                    padding: '5px 12px',
                    borderRadius: 6,
                    fontSize: 11,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    cursor: 'pointer',
                    fontWeight: 700,
                  }}
                  title='Abrir Relatório Detalhado da UF'
                >
                  <FiBarChart2 size={13} /> Relatório da UF
                </button>
              )}
            </>
          ) : (
            <>
              <KpiBadge $highlight={activeUfCount > 0}>
                <span className='dot' />
                Cobertura: <strong>{activeUfCount} / 27 UFs</strong> (
                {((activeUfCount / 27) * 100).toFixed(0)}%)
              </KpiBadge>

              {leaderUf && (
                <KpiBadge>
                  <FaTrophy size={11} color='#eab308' /> Líder: <strong>{leaderUf}</strong> (
                  {safeMap.get(leaderUf)?.toLocaleString('pt-BR')})
                </KpiBadge>
              )}
            </>
          )}
        </KpiGroup>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <RegionSelector>
            {(Object.keys(REGIONS) as RegionKey[]).map((rKey) => {
              const regData = regionalHeatmap[rKey] || { active: 0, potencial: 0, pct: 0 }
              const isSelected = activeRegion === rKey && !focusedUf

              const heatColor =
                regData.pct >= 70
                  ? '#166534'
                  : regData.pct >= 50
                  ? '#22c55e'
                  : regData.pct >= 30
                  ? '#eab308'
                  : regData.pct >= 15
                  ? '#f97316'
                  : regData.pct > 0
                  ? '#ef4444'
                  : '#94a3b8'

              return (
                <RegionButton
                  key={rKey}
                  type='button'
                  $active={isSelected}
                  onClick={() => handleSelectRegion(rKey)}
                  title={`${REGIONS[rKey].name}: ${regData.active.toLocaleString('pt-BR')} PDVs / Potencial: ${regData.potencial.toLocaleString('pt-BR')} (${regData.pct.toFixed(1)}% atingido)`}
                >
                  <span>{REGIONS[rKey].name}</span>
                  <span
                    style={{
                      marginLeft: 6,
                      padding: '2px 5px',
                      borderRadius: 4,
                      fontSize: 10,
                      fontWeight: 700,
                      background: isSelected ? 'rgba(0, 0, 0, 0.22)' : 'rgba(255, 255, 255, 0.08)',
                      color: isSelected ? '#0f172a' : heatColor,
                      border: isSelected ? 'none' : `1px solid ${heatColor}44`,
                    }}
                  >
                    {regData.pct.toFixed(0)}%
                  </span>
                </RegionButton>
              )
            })}
          </RegionSelector>

          {isFullscreen ? (
            <MinimizeButton
              type='button'
              onClick={handleMinimize}
              title='Minimizar Mapa (Voltar ao Painel Normal) [Esc]'
            >
              <FiMinimize2 size={13} /> Minimizar Mapa
            </MinimizeButton>
          ) : (
            <FullscreenButton
              type='button'
              onClick={handleToggleFullscreen}
              title='Expandir para Tela Cheia'
            >
              <FiMaximize2 size={13} /> Expandir
            </FullscreenButton>
          )}
        </div>
      </HeaderBar>

      {/* Controles Flutuantes de Zoom (+ / - / Reset / Tela Cheia) */}
      <FloatingZoomControls>
        <ZoomBtn type='button' onClick={handleZoomIn} title='Aproximar (+)' aria-label='Zoom In'>
          <FiPlus size={16} />
        </ZoomBtn>
        <ZoomBtn type='button' onClick={handleZoomOut} title='Afastar (-)' aria-label='Zoom Out'>
          <FiMinus size={16} />
        </ZoomBtn>
        <ZoomBtn
          type='button'
          onClick={handleResetZoom}
          title='Resetar Visualização do Brasil'
          aria-label='Reset'
        >
          <FiRotateCcw size={15} />
        </ZoomBtn>
        <ZoomBtn
          type='button'
          onClick={handleToggleFullscreen}
          title={isFullscreen ? 'Minimizar Mapa [Esc]' : 'Expandir para Tela Cheia'}
          aria-label='Alternar Tela Cheia'
        >
          {isFullscreen ? <FiMinimize2 size={15} /> : <FiMaximize2 size={15} />}
        </ZoomBtn>
      </FloatingZoomControls>

      {/* Canvas SVG do Mapa */}
      <ComposableMap
        projection='geoMercator'
        projectionConfig={{ scale: isFullscreen ? 1040 : 880, center: [-54, -15] }}
      >
        <defs>
          <filter id='heat-blur' x='-50%' y='-50%' width='200%' height='200%'>
            <feGaussianBlur in='SourceGraphic' stdDeviation='3.2' />
          </filter>
          {/* Gradiente de calor desenhado diretamente na geometria do estado focado seguindo o contorno */}
          <linearGradient id='state-heatmap-gradient' x1='0%' y1='0%' x2='0%' y2='100%'>
            <stop offset='0%' stopColor={stateHeatGradient.northColor} stopOpacity={0.95} />
            <stop offset='35%' stopColor={stateHeatGradient.centerNorthColor} stopOpacity={0.92} />
            <stop offset='70%' stopColor={stateHeatGradient.centerSouthColor} stopOpacity={0.88} />
            <stop offset='100%' stopColor={stateHeatGradient.southColor} stopOpacity={0.95} />
          </linearGradient>
        </defs>

        <ZoomableGroup
          zoom={position.zoom}
          center={position.coordinates}
          onMoveEnd={handleMoveEnd}
          minZoom={0.8}
          maxZoom={7}
        >
          {/* Polígonos dos Estados */}
          <Geographies geography={brazilGeo}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const sigla = geo.properties.sigla
                const count = safeMap.get(sigla) || 0
                const hasActivity = count > 0
                const isFocused = focusedUf === sigla
                const isSelected = selectedUf === sigla

                let fillFinal = getStateColor(count, sigla)
                let strokeColor = hasActivity ? '#000000' : '#475569'
                let strokeWidth = 0.6

                if (focusedUf) {
                  if (isFocused) {
                    fillFinal = 'url(#state-heatmap-gradient)'
                    strokeColor = '#38bdf8'
                    strokeWidth = 2.4
                  } else {
                    fillFinal = '#0f172a'
                    strokeColor = '#1e293b'
                    strokeWidth = 0.5
                  }
                } else if (isSelected) {
                  fillFinal = '#1e3a5f'
                  strokeColor = '#38bdf8'
                  strokeWidth = 1.5
                }

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onClick={() => {
                      if (hasActivity) {
                        handleStateClick(sigla)
                      }
                    }}
                    onMouseMove={(e) => handleMouseMoveState(e, sigla)}
                    onMouseLeave={handleMouseLeaveState}
                    style={{
                      default: {
                        fill: fillFinal,
                        stroke: strokeColor,
                        strokeWidth,
                        outline: 'none',
                        transition: 'fill 0.25s ease, stroke 0.25s ease',
                        cursor: hasActivity ? 'pointer' : 'default',
                        filter: isFocused ? 'drop-shadow(0 0 16px rgba(56, 189, 248, 0.45))' : 'none',
                      },
                      hover: {
                        fill: isFocused
                          ? 'url(#state-heatmap-gradient)'
                          : hasActivity
                          ? '#34d399'
                          : '#3b4863',
                        stroke: isFocused ? '#38bdf8' : hasActivity ? '#ffffff' : '#64748b',
                        strokeWidth: isFocused ? 2.8 : hasActivity ? 1.2 : 0.6,
                        outline: 'none',
                        cursor: hasActivity ? 'pointer' : 'default',
                      },
                      pressed: {
                        fill: isFocused ? 'url(#state-heatmap-gradient)' : '#10b981',
                        outline: 'none',
                      },
                    }}
                  />
                )
              })
            }
          </Geographies>

          {/* Marcadores de Estados (Visíveis apenas quando NÃO está focado em uma UF) */}
          {!focusedUf &&
            Array.from(safeMap.keys()).map((uf) => {
              const coords = UF_COORDINATES[uf]
              const count = safeMap.get(uf) || 0

              if (!coords || count === 0) return null

              const isTop3 = top3Ufs.includes(uf)
              const isSelected = selectedUf === uf

              return (
                <Marker
                  key={`marker-${uf}`}
                  coordinates={coords}
                  onClick={() => handleStateClick(uf)}
                  style={{
                    default: { cursor: 'pointer' },
                    hover: { cursor: 'pointer' },
                    pressed: { cursor: 'pointer' },
                  }}
                >
                  <circle
                    r={isTop3 ? 5.5 : 4}
                    fill={isSelected ? '#38bdf8' : isTop3 ? '#4ade80' : '#0f172a'}
                    stroke='#ffffff'
                    strokeWidth={isTop3 ? 1.5 : 1}
                    style={{
                      filter: isTop3 ? 'drop-shadow(0 0 6px rgba(74, 222, 128, 0.9))' : 'none',
                      transition: 'all 0.2s ease',
                    }}
                  />
                  <text
                    textAnchor='middle'
                    y={-9}
                    style={{
                      fontSize: isTop3 ? 9.5 : 8,
                      fontFamily: '"Montserrat", sans-serif',
                      fontWeight: isTop3 ? 800 : 700,
                      fill: '#ffffff',
                      filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.9))',
                      pointerEvents: 'none',
                    }}
                  >
                    {uf}
                  </text>
                </Marker>
              )
            })}
        </ZoomableGroup>
      </ComposableMap>

      {/* Tooltip Macro do Estado */}
      <TooltipContainer $x={tooltip.x} $y={tooltip.y} $visible={tooltip.visible && !focusedUf}>
        <TooltipHeader>
          <span className='title'>
            <FiMapPin size={13} color='#4ade80' /> {tooltip.name} ({tooltip.uf})
          </span>
          <span className='region-tag'>{tooltip.region}</span>
        </TooltipHeader>

        <TooltipBody>
          <div className='metric-row'>
            <span className='label'>{unitLabel}:</span>
            <span className='value'>
              {tooltip.count > 0 ? tooltip.count.toLocaleString('pt-BR') : 'Sem dados'}
            </span>
          </div>

          <div className='metric-row'>
            <span className='label'>Potencial IBGE (1 PDV / 1k hab):</span>
            <span className='value' style={{ color: '#94a3b8', fontSize: '11px' }}>
              {tooltip.potencial > 0 ? `${tooltip.potencial.toLocaleString('pt-BR')} PDVs` : '—'}
            </span>
          </div>

          <div className='metric-row'>
            <span className='label'>Atingimento Potencial:</span>
            <span
              className='value'
              style={{
                color:
                  tooltip.potencialPct >= 70
                    ? '#4ade80'
                    : tooltip.potencialPct >= 50
                    ? '#86efac'
                    : '#facc15',
                fontSize: '13px',
                fontWeight: 800,
              }}
            >
              {tooltip.potencialPct}%{' '}
              <span style={{ fontSize: '10px', fontWeight: 600 }}>
                {tooltip.potencialPct >= 70
                  ? '(Meta Batida)'
                  : tooltip.potencialPct >= 50
                  ? '(Próximo)'
                  : '(Em Aberto)'}
              </span>
            </span>
          </div>

          {tooltip.count > 0 && (
            <>
              <div className='share-progress'>
                <div
                  className='fill'
                  style={{
                    width: `${Math.min(tooltip.potencialPct, 100)}%`,
                    background:
                      tooltip.potencialPct >= 70
                        ? 'linear-gradient(90deg, #166534, #22c55e)'
                        : tooltip.potencialPct >= 50
                        ? 'linear-gradient(90deg, #22c55e, #4ade80)'
                        : tooltip.potencialPct >= 30
                        ? 'linear-gradient(90deg, #eab308, #facc15)'
                        : tooltip.potencialPct >= 15
                        ? 'linear-gradient(90deg, #f97316, #fb923c)'
                        : 'linear-gradient(90deg, #ef4444, #f87171)',
                    boxShadow:
                      tooltip.potencialPct >= 70
                        ? '0 0 8px rgba(34, 197, 94, 0.7)'
                        : tooltip.potencialPct >= 50
                        ? '0 0 6px rgba(34, 197, 94, 0.5)'
                        : tooltip.potencialPct >= 30
                        ? '0 0 6px rgba(234, 179, 8, 0.5)'
                        : tooltip.potencialPct >= 15
                        ? '0 0 6px rgba(249, 115, 22, 0.5)'
                        : '0 0 6px rgba(239, 68, 68, 0.5)',
                  }}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 4,
                }}
              >
                <span style={{ fontSize: '10px', color: '#cbd5e1', fontWeight: 600 }}>
                  Atingimento: {tooltip.potencialPct}%
                </span>
                <span className='share-text'>{tooltip.share.toFixed(1)}% do Brasil</span>
              </div>
            </>
          )}
        </TooltipBody>

        <TooltipFooter>
          {tooltip.count > 0 ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <FiMousePointer size={11} /> Clique para aproximar e ver PDVs
            </span>
          ) : (
            'Sem positivação neste período'
          )}
        </TooltipFooter>
      </TooltipContainer>

      {/* Tooltip de Cidade no Hover */}
      {cityHover.visible && cityHover.city && (
        <TooltipContainer $x={cityHover.x} $y={cityHover.y} $visible={true}>
          <TooltipHeader>
            <span className='title' style={{ fontSize: 13, fontWeight: 700 }}>
              <FiMapPin size={13} color='#a6c10b' /> {cityHover.city.city} ({cityHover.city.uf})
            </span>
            <span
              className='region-tag'
              style={{
                background:
                  cityHover.city.growthStatus === 'high_opportunity'
                    ? 'rgba(250, 204, 21, 0.2)'
                    : 'rgba(56, 189, 248, 0.2)',
                color:
                  cityHover.city.growthStatus === 'high_opportunity' ? '#facc15' : '#38bdf8',
              }}
            >
              {cityHover.city.growthStatus === 'high_opportunity'
                ? 'Alto Potencial'
                : cityHover.city.growthStatus === 'expanding'
                  ? 'Em Expansão'
                  : 'Consolidado'}
            </span>
          </TooltipHeader>

          <TooltipBody>
            <div className='metric-row'>
              <span className='label'>Cobertura:</span>
              <span className='value' style={{ color: '#38bdf8', fontWeight: 700 }}>
                {cityHover.city.activePdvsCount}{' '}
                {cityHover.city.activePdvsCount === 1 ? 'PDV Ativo' : 'PDVs Ativos'}
              </span>
            </div>

            <div className='metric-row'>
              <span className='label'>Total {isVolumeMode ? 'Volume' : 'Vendas'}:</span>
              <span className='value' style={{ color: '#ffffff', fontWeight: 800 }}>
                {isVolumeMode
                  ? `${cityHover.city.totalVolume.toLocaleString('pt-BR')} CX`
                  : new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(cityHover.city.totalValue)}
              </span>
            </div>

            <div className='metric-row'>
              <span className='label'>Ticket Médio / PDV:</span>
              <span className='value' style={{ color: '#a6c10b', fontWeight: 700 }}>
                {isVolumeMode
                  ? `${cityHover.city.avgTicket.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} CX`
                  : new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(cityHover.city.avgTicket)}
              </span>
            </div>

            <div className='metric-row'>
              <span className='label'>Média SKUs / PDV:</span>
              <span className='value' style={{ color: '#facc15', fontWeight: 700 }}>
                {cityHover.city.avgSkusPerPdv.toFixed(1)} SKUs
              </span>
            </div>
          </TooltipBody>

          <TooltipFooter>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <FiMousePointer size={11} /> Clique para ver o diagnóstico e PDVs
            </span>
          </TooltipFooter>
        </TooltipContainer>
      )}

      {/* Modal de Inteligência da Cidade */}
      {isFullscreen && selectedCity && (
        <CityDetailsModal
          cityData={selectedCity}
          onClose={() => setSelectedCity(null)}
          isVolumeMode={isVolumeMode}
        />
      )}

      {/* Card de Concentração Térmica e Potencial por Região */}
      {isFullscreen && focusedUf && (
        <NorthSouthCard>
          <div className='ns-header' style={{ justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <FiCompass size={13} color='#38bdf8' />
              <span>Potencial por Região ({focusedUf})</span>
            </div>
            <span style={{ fontSize: 9, color: '#94a3b8', fontWeight: 500 }}>Clique p/ filtrar</span>
          </div>
          <div className='ns-bars'>
            {/* Norte */}
            <div
              className='ns-row'
              onClick={() => {
                setPanelRegionFilter('Norte')
                handleToggleSidePanel(true)
              }}
              title='Filtrar Cidades da Região Norte no Painel Lateral'
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <span className='ns-label' style={{ color: '#38bdf8', fontWeight: 700, fontSize: 11 }}>
                  Norte
                </span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: regionalBreakdown.norte.atingimento < 70 ? '#f59e0b' : '#10b981',
                  }}
                >
                  {regionalBreakdown.norte.atingimento}% do Potencial
                </span>
              </div>
              <div className='ns-track'>
                <div
                  className='ns-fill'
                  style={{
                    width: `${Math.min(100, Math.max(4, regionalBreakdown.norte.atingimento))}%`,
                    background: regionalBreakdown.norte.atingimento < 70 ? '#f59e0b' : '#10b981',
                  }}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 9,
                  color: '#94a3b8',
                }}
              >
                <span>
                  {regionalBreakdown.norte.pdvs} / {regionalBreakdown.norte.potencial} PDVs
                </span>
                <span style={{ color: '#cbd5e1' }}>
                  {regionalBreakdown.norte.share}% da base da UF
                </span>
              </div>
            </div>

            {/* Centro */}
            <div
              className='ns-row'
              onClick={() => {
                setPanelRegionFilter('Centro')
                handleToggleSidePanel(true)
              }}
              title='Filtrar Cidades da Região Centro no Painel Lateral'
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <span className='ns-label' style={{ color: '#38bdf8', fontWeight: 700, fontSize: 11 }}>
                  Centro
                </span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: regionalBreakdown.centro.atingimento < 70 ? '#f59e0b' : '#10b981',
                  }}
                >
                  {regionalBreakdown.centro.atingimento}% do Potencial
                </span>
              </div>
              <div className='ns-track'>
                <div
                  className='ns-fill'
                  style={{
                    width: `${Math.min(100, Math.max(4, regionalBreakdown.centro.atingimento))}%`,
                    background: regionalBreakdown.centro.atingimento < 70 ? '#f59e0b' : '#10b981',
                  }}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 9,
                  color: '#94a3b8',
                }}
              >
                <span>
                  {regionalBreakdown.centro.pdvs} / {regionalBreakdown.centro.potencial} PDVs
                </span>
                <span style={{ color: '#cbd5e1' }}>
                  {regionalBreakdown.centro.share}% da base da UF
                </span>
              </div>
            </div>

            {/* Sul */}
            <div
              className='ns-row'
              onClick={() => {
                setPanelRegionFilter('Sul')
                handleToggleSidePanel(true)
              }}
              title='Filtrar Cidades da Região Sul no Painel Lateral'
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <span className='ns-label' style={{ color: '#38bdf8', fontWeight: 700, fontSize: 11 }}>
                  Sul
                </span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: regionalBreakdown.sul.atingimento < 70 ? '#f59e0b' : '#10b981',
                  }}
                >
                  {regionalBreakdown.sul.atingimento}% do Potencial
                </span>
              </div>
              <div className='ns-track'>
                <div
                  className='ns-fill'
                  style={{
                    width: `${Math.min(100, Math.max(4, regionalBreakdown.sul.atingimento))}%`,
                    background: regionalBreakdown.sul.atingimento < 70 ? '#f59e0b' : '#10b981',
                  }}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 9,
                  color: '#94a3b8',
                }}
              >
                <span>
                  {regionalBreakdown.sul.pdvs} / {regionalBreakdown.sul.potencial} PDVs
                </span>
                <span style={{ color: '#cbd5e1' }}>
                  {regionalBreakdown.sul.share}% da base da UF
                </span>
              </div>
            </div>
          </div>

          {/* Gradiente Térmico Contínuo Norte -> Sul */}
          <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <span style={{ fontSize: 9, color: '#94a3b8', fontWeight: 600, display: 'block', marginBottom: 4 }}>
              MAPA DE CALOR ({focusedUf})
            </span>
            <div
              style={{
                width: '100%',
                height: 8,
                borderRadius: 4,
                background: `linear-gradient(90deg, ${stateHeatGradient.northColor}, ${stateHeatGradient.centerNorthColor}, ${stateHeatGradient.centerSouthColor}, ${stateHeatGradient.southColor})`,
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
              <span style={{ fontSize: 8, color: '#94a3b8', fontWeight: 600 }}>Norte</span>
              <span style={{ fontSize: 8, color: '#94a3b8', fontWeight: 600 }}>Sul</span>
            </div>
            <span style={{ fontSize: 7.5, color: '#64748b', display: 'block', marginTop: 2 }}>
              Verde = alta presença | Grafite = baixa cobertura
            </span>
          </div>
        </NorthSouthCard>
      )}

      {/* Legenda Dinâmica Única (apenas no mapa geral do Brasil) */}
      {!focusedUf && (
        <LegendBox>
          <span className='title'>Atingimento Potencial IBGE</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
            <div
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
            >
              <span style={{ width: 22, height: 7, background: '#2a354b', borderRadius: 2 }} />
              <span style={{ fontSize: 8, color: '#64748b' }}>0%</span>
            </div>
            <div
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
            >
              <span style={{ width: 22, height: 7, background: '#ef4444', borderRadius: 2 }} />
              <span style={{ fontSize: 8, color: '#94a3b8' }}>&lt;15%</span>
            </div>
            <div
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
            >
              <span style={{ width: 22, height: 7, background: '#f97316', borderRadius: 2 }} />
              <span style={{ fontSize: 8, color: '#94a3b8' }}>15-30%</span>
            </div>
            <div
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
            >
              <span style={{ width: 22, height: 7, background: '#eab308', borderRadius: 2 }} />
              <span style={{ fontSize: 8, color: '#94a3b8' }}>30-50%</span>
            </div>
            <div
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
            >
              <span style={{ width: 22, height: 7, background: '#22c55e', borderRadius: 2 }} />
              <span style={{ fontSize: 8, color: '#94a3b8' }}>50-70%</span>
            </div>
            <div
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
            >
              <span
                style={{
                  width: 22,
                  height: 7,
                  background: '#166534',
                  borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.3)',
                }}
              />
              <span style={{ fontSize: 8, color: '#4ade80', fontWeight: 700 }}>+70%</span>
            </div>
          </div>
          <span style={{ fontSize: 8, color: '#64748b', marginTop: 2 }}>
            Verde escuro = meta batida (+70%) | Vermelho = crítico (&lt;15%) | Grafite = sem cobertura
          </span>
        </LegendBox>
      )}

      {/* PAINEL DE INTELIGÊNCIA DO ESTADO (INFORMAÇÕES QUE ESTAVAM NO MODAL) */}
      {isFullscreen && focusedUf && (
        <>
          {!showSidePanel && (
            <PanelToggleBtn type='button' onClick={() => handleToggleSidePanel(true)}>
              <FiBarChart2 size={13} /> Ver Detalhes de {focusedUf}
            </PanelToggleBtn>
          )}

          <StateSidePanel $open={showSidePanel}>
            <PanelHeader>
              <div className='title-group'>
                <h3>
                  <FiMapPin size={16} color='#38bdf8' />
                  {focusedUf} — {UF_METADATA[focusedUf]?.name || focusedUf}
                </h3>
                <span className='sub'>
                  {panelRegionFilter === 'TODOS'
                    ? 'Todo o Estado'
                    : `Região ${panelRegionFilter}`}{' '}
                  • {regionFilteredCities.length} Cidades • {macroRegionStats.totalPop.toLocaleString('pt-BR')} Habitantes (IBGE)
                </span>
              </div>
              <div className='actions'>
                {onStateClick && (
                  <button
                    type='button'
                    onClick={() => onStateClick(focusedUf)}
                    style={{
                      background: 'rgba(56, 189, 248, 0.15)',
                      border: '1px solid rgba(56, 189, 248, 0.4)',
                      color: '#38bdf8',
                      padding: '4px 10px',
                      borderRadius: 6,
                      fontSize: 10,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                    title='Abrir em Janela Separada'
                  >
                    Janela Externa
                  </button>
                )}
                <button
                  type='button'
                  onClick={() => handleToggleSidePanel(false)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.06)',
                    border: 'none',
                    color: '#94a3b8',
                    padding: 6,
                    borderRadius: 6,
                    cursor: 'pointer',
                    display: 'flex',
                  }}
                  title='Recolher Painel'
                >
                  <FiX size={16} />
                </button>
              </div>
            </PanelHeader>

            {/* Barra de Filtro Regional Segmentada: Todos, Norte, Centro, Sul */}
            <PanelRegionBar>
              <PanelRegionPillBtn
                type='button'
                $active={panelRegionFilter === 'TODOS'}
                $color='#38bdf8'
                onClick={() => setPanelRegionFilter('TODOS')}
              >
                <div className='pill-title'>
                  <span className='pill-dot' style={{ background: '#38bdf8' }} />
                  <span>Todos</span>
                </div>
                <span className='pill-sub'>
                  {stateCities.length} cid • {stateTotalPdvs} pdvs
                </span>
              </PanelRegionPillBtn>

              <PanelRegionPillBtn
                type='button'
                $active={panelRegionFilter === 'Norte'}
                $color='#10b981'
                onClick={() => setPanelRegionFilter('Norte')}
              >
                <div className='pill-title'>
                  <span className='pill-dot' style={{ background: '#10b981' }} />
                  <span>Norte</span>
                </div>
                <span className='pill-sub'>
                  {regionCounts.norteCid} cid • {regionCounts.nortePdvs} pdvs
                </span>
              </PanelRegionPillBtn>

              <PanelRegionPillBtn
                type='button'
                $active={panelRegionFilter === 'Centro'}
                $color='#eab308'
                onClick={() => setPanelRegionFilter('Centro')}
              >
                <div className='pill-title'>
                  <span className='pill-dot' style={{ background: '#eab308' }} />
                  <span>Centro</span>
                </div>
                <span className='pill-sub'>
                  {regionCounts.centroCid} cid • {regionCounts.centroPdvs} pdvs
                </span>
              </PanelRegionPillBtn>

              <PanelRegionPillBtn
                type='button'
                $active={panelRegionFilter === 'Sul'}
                $color='#0ea5e9'
                onClick={() => setPanelRegionFilter('Sul')}
              >
                <div className='pill-title'>
                  <span className='pill-dot' style={{ background: '#0ea5e9' }} />
                  <span>Sul</span>
                </div>
                <span className='pill-sub'>
                  {regionCounts.sulCid} cid • {regionCounts.sulPdvs} pdvs
                </span>
              </PanelRegionPillBtn>
            </PanelRegionBar>

            {/* Conteúdo Principal do Painel */}
            <PanelBody>
              {loadingCities ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13 }}>
                  Carregando dados de {focusedUf}...
                </div>
              ) : (
                <>
                  {/* Card Executivo Demográfico & Potencial IBGE */}
                  <DemographicKpiBox>
                    <div className='demo-header'>
                      <div className='demo-title'>
                        <FiCompass size={15} />
                        <span>
                          Potencial & Demografia IBGE — {panelRegionFilter === 'TODOS' ? 'Todo o Estado' : `Região ${panelRegionFilter}`}
                        </span>
                      </div>
                      <span
                        className='status-pill'
                        style={{
                          background: macroRegionStats.isBelow70
                            ? 'rgba(245, 158, 11, 0.22)'
                            : 'rgba(16, 185, 129, 0.22)',
                          color: macroRegionStats.isBelow70 ? '#facc15' : '#4ade80',
                          border: `1px solid ${
                            macroRegionStats.isBelow70
                              ? 'rgba(245, 158, 11, 0.45)'
                              : 'rgba(16, 185, 129, 0.45)'
                          }`,
                        }}
                      >
                        {macroRegionStats.isBelow70
                          ? `${macroRegionStats.atingimentoPct}% • Abaixo de 70% (Oportunidade)`
                          : `${macroRegionStats.atingimentoPct}% • Meta Batida (≥ 70%)`}
                      </span>
                    </div>

                    <div className='demo-metrics'>
                      <div className='item'>
                        <span className='item-label'>População Total (IBGE)</span>
                        <strong className='item-val'>
                          {macroRegionStats.totalPop.toLocaleString('pt-BR')}{' '}
                          <small style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>hab</small>
                        </strong>
                      </div>
                      <div className='item'>
                        <span className='item-label'>Potencial de PDVs</span>
                        <strong className='item-val'>
                          {macroRegionStats.totalPotencialPdvs.toLocaleString('pt-BR')}{' '}
                          <small style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>PDVs (1/1k)</small>
                        </strong>
                      </div>
                      <div className='item'>
                        <span className='item-label'>PDVs Atendidos</span>
                        <strong className='item-val' style={{ color: '#a6c10b' }}>
                          {macroRegionStats.totalPdvs.toLocaleString('pt-BR')}
                        </strong>
                      </div>
                      <div className='item'>
                        <span className='item-label'>% Atingimento</span>
                        <strong
                          className='item-val'
                          style={{ color: macroRegionStats.isBelow70 ? '#facc15' : '#4ade80' }}
                        >
                          {macroRegionStats.atingimentoPct}%
                        </strong>
                      </div>
                    </div>

                    <div className='progress-wrap'>
                      <div className='bar-track'>
                        <div
                          className='bar-fill'
                          style={{
                            width: `${Math.min(100, macroRegionStats.atingimentoPct)}%`,
                            background: macroRegionStats.isBelow70 ? '#f59e0b' : '#10b981',
                          }}
                        />
                        <div className='target-marker' title='Meta de 70%' />
                      </div>
                      <div className='bar-legend'>
                        <span>
                          Cobertura Atual: <strong>{macroRegionStats.atingimentoPct}%</strong>
                        </span>
                        <span className='marker-label'>Meta Mínima: 70%</span>
                      </div>
                    </div>

                    <div className='demo-secondary-grid'>
                      <div className='sub-item'>
                        <span className='sub-lbl'>MÉDIA DE SKUs / PDV</span>
                        <strong className='sub-val' style={{ color: '#facc15' }}>
                          {macroRegionStats.avgSkus} SKUs
                        </strong>
                      </div>
                      <div className='sub-item'>
                        <span className='sub-lbl'>
                          {isVolumeMode ? 'VOLUME MÉDIO / PDV' : 'TICKET MÉDIO / PDV'}
                        </span>
                        <strong className='sub-val' style={{ color: '#a6c10b' }}>
                          {isVolumeMode
                            ? `${macroRegionStats.avgTicket.toFixed(1)} CX`
                            : new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                                maximumFractionDigits: 0,
                              }).format(macroRegionStats.avgTicket)}
                        </strong>
                      </div>
                      <div className='sub-item'>
                        <span className='sub-lbl'>
                          {isVolumeMode ? 'VOLUME TOTAL DA REGIÃO' : 'FATURAMENTO TOTAL DA REGIÃO'}
                        </span>
                        <strong className='sub-val' style={{ color: '#ffffff' }}>
                          {isVolumeMode
                            ? `${macroRegionStats.totalVol.toLocaleString('pt-BR', {
                                maximumFractionDigits: 1,
                              })} CX`
                            : new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                                maximumFractionDigits: 0,
                              }).format(macroRegionStats.totalVal)}
                        </strong>
                      </div>
                    </div>
                  </DemographicKpiBox>

                  {/* Plano de Ação Estratégico Comercial */}
                  <ActionRecommendationCard $status={macroRegionStats.statusType}>
                    <div className='rec-header'>
                      <div className='title'>
                        <FiTrendingUp size={15} />
                        <span>
                          O Que Precisamos Fazer{' '}
                          {panelRegionFilter === 'TODOS'
                            ? 'no Estado'
                            : `no ${panelRegionFilter}`}
                        </span>
                      </div>
                      <span className='badge'>
                        {macroRegionStats.isBelow70
                          ? 'Abaixo de 70% • Oportunidade'
                          : 'Meta Batida (≥ 70%)'}
                      </span>
                    </div>
                    <div className='rec-body'>{macroRegionStats.actionRecommendation}</div>
                  </ActionRecommendationCard>

                  {/* Barra de Busca de Cidades */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      marginTop: 4,
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        background: 'rgba(0,0,0,0.3)',
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                      }}
                    >
                      <FiSearch size={14} color='#94a3b8' />
                      <input
                        type='text'
                        placeholder={`Buscar Cidade em ${
                          panelRegionFilter === 'TODOS'
                            ? focusedUf
                            : `${focusedUf} (${panelRegionFilter})`
                        }...`}
                        value={panelCitySearch}
                        onChange={(e) => setPanelCitySearch(e.target.value)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#ffffff',
                          fontSize: 12,
                          outline: 'none',
                          width: '100%',
                        }}
                      />
                      {panelCitySearch && (
                        <button
                          type='button'
                          onClick={() => setPanelCitySearch('')}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#94a3b8',
                            cursor: 'pointer',
                          }}
                        >
                          <FiX size={14} />
                        </button>
                      )}
                    </div>
                    <span style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                      Exibindo <strong>{filteredPanelCities.length}</strong> de{' '}
                      <strong>{regionFilteredCities.length}</strong> cidades
                    </span>
                  </div>

                  {/* Lista de Cidades da Região Selecionada */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {filteredPanelCities.map((city) => (
                      <CityCardItem key={city.city}>
                        <div className='city-top'>
                          <div className='city-name-group'>
                            <span className='city-title'>{city.city}</span>
                            {city.macroRegion && (
                              <CityMacroBadge $region={city.macroRegion}>
                                {city.macroRegion}
                              </CityMacroBadge>
                            )}
                          </div>
                          <span className='city-vol-badge'>
                            {isVolumeMode
                              ? `${city.totalVolume.toLocaleString('pt-BR', {
                                  maximumFractionDigits: 1,
                                })} CX`
                              : new Intl.NumberFormat('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL',
                                  maximumFractionDigits: 0,
                                }).format(city.totalValue)}
                          </span>
                        </div>

                        <div className='city-metrics-grid'>
                          <div className='stat-col'>
                            <span className='stat-lbl'>População (IBGE)</span>
                            <span className='stat-val'>
                              {city.population ? `${city.population.toLocaleString('pt-BR')} hab` : '—'}
                            </span>
                          </div>
                          <div className='stat-col'>
                            <span className='stat-lbl'>PDVs Atendidos / Pot.</span>
                            <span className='stat-val'>
                              {city.activePdvsCount} / {city.potencialPdvs || '—'}
                            </span>
                          </div>
                          <div className='stat-col'>
                            <span className='stat-lbl'>Atingimento Potencial</span>
                            <span
                              className='stat-val'
                              style={{
                                color:
                                  (city.potencialPct || 0) < 70 ? '#f59e0b' : '#10b981',
                              }}
                            >
                              {city.potencialPct || 0}%{' '}
                              <small style={{ fontSize: 10, fontWeight: 600 }}>
                                {(city.potencialPct || 0) < 70 ? '(< 70%)' : '(≥ 70%)'}
                              </small>
                            </span>
                          </div>
                          <div className='stat-col'>
                            <span className='stat-lbl'>Média de SKUs</span>
                            <span className='stat-val'>
                              {city.avgSkusPerPdv.toFixed(1)}{' '}
                              <small style={{ fontSize: 10, color: '#94a3b8' }}>
                                ({city.uniqueSkusCount} mix)
                              </small>
                            </span>
                          </div>
                        </div>

                        <div className='city-bottom'>
                          <div className='city-meta'>
                            Ticket Médio:{' '}
                            <strong style={{ color: '#e2e8f0' }}>
                              {isVolumeMode
                                ? `${city.avgTicket.toFixed(1)} CX/PDV`
                                : new Intl.NumberFormat('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                    maximumFractionDigits: 0,
                                  }).format(city.avgTicket)}
                            </strong>
                          </div>
                          <div className='city-actions'>
                            <button
                              type='button'
                              className='btn-city-details'
                              onClick={() => setSelectedCity(city)}
                              title={`Ver Detalhes, PDVs e Produtos de ${city.city}`}
                            >
                              <span>Ver Detalhes da Cidade</span>
                              <FiArrowRight size={13} />
                            </button>
                          </div>
                        </div>
                      </CityCardItem>
                    ))}
                    {filteredPanelCities.length === 0 && (
                      <div
                        style={{
                          textAlign: 'center',
                          padding: 32,
                          color: '#64748b',
                          fontSize: 12,
                        }}
                      >
                        Nenhum município encontrado na região {panelRegionFilter} para o termo
                        digitado.
                      </div>
                    )}
                  </div>
                </>
              )}
            </PanelBody>
          </StateSidePanel>
        </>
      )}
    </MapContainer>
  )
}

export default MapBrazil
